import {apiConstants, config, downloadBlob, requestOptions, requestTypes, sendRequest} from "../../../../Utilities/Api";

const BRAILLER_LOAN_API_URL = `${config.URL}braillers/loans`;

// Helper to filter out null, undefined, and empty string values before creating query params
const createQueryParams = (criteria: Record<string, unknown>) => {
    const filtered = Object.entries(criteria)
        .filter(([, value]) => value !== null && value !== undefined && value !== "")
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
    return new URLSearchParams(filtered as Record<string, string>).toString();
};

class serverBraillerLoanApi {
    static getLoanHistory(searchCriteria: Record<string, unknown>) {
        const queryParameters = createQueryParams(searchCriteria);
        return sendRequest(
            requestOptions(
                `${BRAILLER_LOAN_API_URL}/history?${queryParameters}`,
                requestTypes.GET,
            ),
        );
    }

    static getRecentLoans(take: number = 10) {
        return sendRequest(
            requestOptions(
                `${BRAILLER_LOAN_API_URL}/recent?take=${take}`,
                requestTypes.GET,
            ),
        );
    }

    static getLoansReadyForProcessing(filters: Record<string, unknown>) {
        const queryParameters = createQueryParams(filters);
        return sendRequest(
            requestOptions(
                `${BRAILLER_LOAN_API_URL}/ready-for-processing?${queryParameters}`,
                requestTypes.GET,
            ),
        );
    }

    static getOverdueLoans() {
        return sendRequest(
            requestOptions(`${BRAILLER_LOAN_API_URL}/overdue`,
                requestTypes.GET,
            ),
        );
    }

    // NEW METHOD: Get unified view of loans AND repairs (including unloaned repairs)
    static getLoansAndRepairsForManagement(searchCriteria: Record<string, unknown>) {
        const queryParameters = createQueryParams(searchCriteria);
        return sendRequest(
            requestOptions(
                `${BRAILLER_LOAN_API_URL}/management?${queryParameters}`,
                requestTypes.GET,
            ),
        );
    }

    static updateLoans(loanItems: Record<string, unknown>[]) {
        return sendRequest(
            requestOptions(BRAILLER_LOAN_API_URL,
                requestTypes.PUT,
                JSON.stringify(loanItems),
                apiConstants.successMessage.brailler.LOAN_UPDATED,
            ),
        );
    }

    static returnItem(returnDto: Record<string, unknown>) {
        return sendRequest(
            requestOptions(`${BRAILLER_LOAN_API_URL}/return`,
                requestTypes.POST,
                JSON.stringify(returnDto),
                apiConstants.successMessage.brailler.ITEM_RETURNED,
            ),
        );
    }

    static renewLoan(renewDto: Record<string, unknown>) {
        return sendRequest(
            requestOptions(`${BRAILLER_LOAN_API_URL}/renew`,
                requestTypes.POST,
                JSON.stringify(renewDto),
                apiConstants.successMessage.brailler.LOAN_RENEWED,
            ),
        );
    }

    static cancelLoan(loanId: string, notes: string | null = null) {
        return sendRequest(
            requestOptions(`${BRAILLER_LOAN_API_URL}/${loanId}/cancel`,
                requestTypes.PUT,
                JSON.stringify({ notes }),
                apiConstants.successMessage.brailler.LOAN_CANCELLED,
            ),
        );
    }

    static getDropdownListsForBraillerLoanSearch() {
        const isForLoanManagement = true;
        const queryParameters = new URLSearchParams({isForLoanManagement: String(isForLoanManagement)}).toString();
        return sendRequest(
            requestOptions(`${BRAILLER_LOAN_API_URL}/dropdown-lists?${queryParameters}`,
                requestTypes.GET,
            ),
        );
    }

    static getBraillerLoansReport(braillerLoanSearchCriteria: Record<string, unknown>) {
        const queryParameters = createQueryParams(braillerLoanSearchCriteria);
        return downloadBlob(
            requestOptions(config.URL + `braillers/reports/loan/export?${queryParameters}`,
                requestTypes.GET,
                null,
                apiConstants.successMessage.report.REPORT_DOWNLOADED,
            ),
        );
    }

    static getBraillerLoanHistoryReport(braillerLoanHistorySearchCriteria: Record<string, unknown>) {
        const queryParameters = createQueryParams(braillerLoanHistorySearchCriteria);
        return downloadBlob(
            requestOptions(config.URL + `braillers/reports/loan-history/export?${queryParameters}`,
                requestTypes.GET,
                null,
                apiConstants.successMessage.report.REPORT_DOWNLOADED,
            ),
        );
    }

    static validateSerialNumberSwap(loanId: string, newSerialNumber: string) {
        return sendRequest(
            requestOptions(
                `${BRAILLER_LOAN_API_URL}/${loanId}/serial-number-swap/validate`,
                requestTypes.POST,
                JSON.stringify({ loanId, newSerialNumber }),
            ),
        );
    }

    static executeSerialNumberSwap(loanId: string, swapRequest: Record<string, unknown>) {
        return sendRequest(
            requestOptions(
                `${BRAILLER_LOAN_API_URL}/${loanId}/serial-number-swap/execute`,
                requestTypes.POST,
                JSON.stringify(swapRequest),
                apiConstants.successMessage.brailler.LOAN_UPDATED,
            ),
        );
    }
}

export const braillerLoanApi = config.environment.MODE === config.modes.SERVER ? serverBraillerLoanApi : serverBraillerLoanApi;